import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SessionManager } from '../sessionManager';

export interface UserPermissions {
  [key: string]: any;
}

export interface TenantInfo {
  id: string;
  name: string;
  role: string;
  member_since: string;
  is_active: boolean;
}

export interface UserProfile {
  id: string | null;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  role: string | null;
  tenant: string | null; // Legacy field, kept for backward compatibility
  active_tenant: TenantInfo | null;
  tenants: TenantInfo[] | null;
  activeSite: string | null;
  sessionId: string | null;
  permissions: UserPermissions | null;
  isActive: boolean | null;
  is_platform_admin: boolean | null;
}

export interface UpdateProfilePayload {
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  role?: string | null;
}

export interface AuthState {
  backendAccessToken: string | null;
  tokenType: string | null;
  expiresIn: number | null;
  isAuthenticated: boolean;
  profile: UserProfile;
}

const persisted = SessionManager.load();

const initialState: AuthState = {
  backendAccessToken: persisted?.backendAccessToken ?? null,
  tokenType: persisted?.tokenType ?? null,
  expiresIn: persisted?.expiresIn ?? null,
  isAuthenticated: Boolean(persisted?.backendAccessToken),
  profile: persisted?.profile ?? {
    id: null,
    email: null,
    firstName: null,
    lastName: null,
    phone: null,
    role: null,
    tenant: null,
    active_tenant: null,
    tenants: null,
    activeSite: null,
    sessionId: null,
    permissions: null,
    isActive: null,
    is_platform_admin: null,
  },
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setBackendToken: (
      state,
      action: PayloadAction<{ accessToken: string; tokenType?: string | null; expiresIn?: number | null }>
    ) => {
      state.backendAccessToken = action.payload.accessToken;
      state.tokenType = action.payload.tokenType ?? 'Bearer';
      state.expiresIn = action.payload.expiresIn ?? null;
      state.isAuthenticated = true;
      SessionManager.save({
        backendAccessToken: state.backendAccessToken,
        tokenType: state.tokenType,
        expiresIn: state.expiresIn,
        profile: state.profile,
      });
    },
    setProfile: (state, action: PayloadAction<UserProfile>) => {
      state.profile = action.payload;
      state.isAuthenticated = true;
      SessionManager.save({
        backendAccessToken: state.backendAccessToken,
        tokenType: state.tokenType,
        expiresIn: state.expiresIn,
        profile: state.profile,
      });
    },
    updateProfileFields: (state, action: PayloadAction<UpdateProfilePayload>) => {
      state.profile = {
        ...state.profile,
        firstName: action.payload.firstName ?? state.profile.firstName,
        lastName: action.payload.lastName ?? state.profile.lastName,
        phone: action.payload.phone ?? state.profile.phone,
        role: action.payload.role ?? state.profile.role,
      };
      SessionManager.save({
        backendAccessToken: state.backendAccessToken,
        tokenType: state.tokenType,
        expiresIn: state.expiresIn,
        profile: state.profile,
      });
    },
    setActiveTenant: (state, action: PayloadAction<TenantInfo>) => {
      state.profile.active_tenant = action.payload;
      SessionManager.save({
        backendAccessToken: state.backendAccessToken,
        tokenType: state.tokenType,
        expiresIn: state.expiresIn,
        profile: state.profile,
      });
    },
    logout: (state) => {
      state.backendAccessToken = null;
      state.tokenType = null;
      state.expiresIn = null;
      state.isAuthenticated = false;
      state.profile = {
        id: null,
        email: null,
        firstName: null,
        lastName: null,
        phone: null,
        role: null,
        tenant: null,
        active_tenant: null,
        tenants: null,
        activeSite: null,
        sessionId: null,
        permissions: null,
        isActive: null,
        is_platform_admin: null,
      };
      SessionManager.clear();
    },
  },
});

export const { setBackendToken, setProfile, updateProfileFields, logout, setActiveTenant } = authSlice.actions;
export default authSlice.reducer;


